import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../context/AuthContext'
import { Plus, Pencil, Trash2, X, CheckCircle, Clock, DollarSign, TrendingDown, Search, Settings2, ExternalLink, FileText } from 'lucide-react'
import api from '../api/axios'
import toast from 'react-hot-toast'

const PAYMENT_METHODS = ['Cash', 'Company Debit Card', 'Personal Card', 'UPI', 'Internet Banking_AXIS', 'Internet Banking_IDFC', 'Cheque_AXIS', 'Cheque_IDFC', 'Auto Debit_IDFC', 'Auto Debit_AXIS']
const RECEIPT_TYPES = ['Digital Copy', 'Physical Copy', 'No']
const DIRECTORS = ['Karthikeyan', 'Veda Prakash', 'Prabhakaran', 'Ganesh Kumar']
const SPENT_BY = ['Company', 'Karthikeyan', 'Veda Prakash', 'Prabhakaran', 'Ganesh Kumar']
const CATEGORIES = ['Rent', 'Office Supplies', 'Internet', 'Electricity Bills', 'Salaries', 'Software', 'Subscriptions', 'Computer Parts', 'Computer', 'Restaurant Bills', 'Bank Charges', 'Tax', 'Travel', 'Marketing', 'Miscellaneous']
const CLIENTS = ['PinSource', 'Ashworth', 'Steelberry', 'PinsFast', 'Monterey', 'Stride Awards', 'Others']
const TRANSACTION_MODES = ['Wire Transfer', 'PayPal', 'Others']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const EMPTY_EXPENSE = { vendorInvoiceNo: '', date: '', description: '', amount: '', paymentMethod: '', receiptType: 'No', authorizedBy: '', spentBy: 'Company', status: 'Pending', billUrl: '', category: '', notes: '' }
const EMPTY_INVOICE = { invoiceNo: '', clientName: '', modeOfTransaction: '', invoicingPeriod: '', invoiceAmount: '', status: 'Pending', receivedAmount: '', raisedOn: '', creditedInBank: '', receivedOn: '', notes: '' }

function StatCard({ label, value, sub, color, icon: Icon }: any) {
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-lg font-bold text-navy-900 dark:text-white">{value}</p>
        {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
      </div>
    </div>
  )
}

export default function FinancePage() {
  const { hasPermission } = useAuth()
  const canExpense = hasPermission('manage_expenses') || hasPermission('enter_expenses')
  const canManageExpense = hasPermission('manage_expenses')
  const canInvoice = hasPermission('manage_invoices')
  const canExpAnalytics = hasPermission('view_expense_analytics')
  const canInvAnalytics = hasPermission('view_invoice_analytics')
  const defaultTab = canExpAnalytics ? 'exp-analytics' : canInvAnalytics ? 'inv-analytics' : canExpense ? 'expenses' : 'invoices'
  const [activeTab, setActiveTab] = useState<'expenses' | 'invoices' | 'exp-analytics' | 'inv-analytics'>(defaultTab as any)
  const [analyticsData, setAnalyticsData] = useState<any>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [yearType, setYearType] = useState<'FY' | 'CY'>('FY')
  const defaultAnalyticsYear = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1
  const [analyticsYear, setAnalyticsYear] = useState(defaultAnalyticsYear)
  const [analyticsYearB, setAnalyticsYearB] = useState(defaultAnalyticsYear - 1)
  const [selectedClient, setSelectedClient] = useState<string>('')
  const [allYearsData, setAllYearsData] = useState<any>(null)
  const [allYearsLoading, setAllYearsLoading] = useState(false)
  const [summary, setSummary] = useState<any>(null)

  // Expenses state
  const [expenses, setExpenses] = useState<any[]>([])
  const [expTotal, setExpTotal] = useState(0)
  const [expPage, setExpPage] = useState(1)
  const [expStatus, setExpStatus] = useState('')
  const [expCategory, setExpCategory] = useState('')
  const [expPaymentMethod, setExpPaymentMethod] = useState('')
  const [expDateFrom, setExpDateFrom] = useState('')
  const [expDateTo, setExpDateTo] = useState('')

  const setExpThisMonth = () => {
    const now = new Date()
    setExpDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10))
    setExpDateTo(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10))
    setExpPage(1)
  }
  const setExpLastMonth = () => {
    const now = new Date()
    setExpDateFrom(new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10))
    setExpDateTo(new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10))
    setExpPage(1)
  }
  const clearExpDates = () => { setExpDateFrom(''); setExpDateTo(''); setExpPage(1) }
  const [showExpForm, setShowExpForm] = useState(false)
  const [editingExp, setEditingExp] = useState<any>(null)
  const [expForm, setExpForm] = useState({ ...EMPTY_EXPENSE })
  const [expSaving, setExpSaving] = useState(false)
  const [selectedExpIds, setSelectedExpIds] = useState<Set<string>>(new Set())
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [expSpentBy, setExpSpentBy] = useState('')
  const [spentByPending, setSpentByPending] = useState<Record<string, { amount: number, count: number }>>({})
  const [viewingExp, setViewingExp] = useState<any>(null)

  // Invoices state
  const [invoices, setInvoices] = useState<any[]>([])
  const [invTotal, setInvTotal] = useState(0)
  const [invPage, setInvPage] = useState(1)
  const [invStatus, setInvStatus] = useState('')
  const [invClient, setInvClient] = useState('')
  const [invMode, setInvMode] = useState('')
  const [invPeriodYear, setInvPeriodYear] = useState('')
  const [invPeriodMonth, setInvPeriodMonth] = useState('')

  const setInvThisMonth = () => {
    const now = new Date()
    setInvPeriodYear(String(now.getFullYear()))
    setInvPeriodMonth(MONTHS[now.getMonth()])
    setInvPage(1)
  }
  const setInvThisYear = () => {
    setInvPeriodYear(String(new Date().getFullYear()))
    setInvPeriodMonth('')
    setInvPage(1)
  }
  const clearInvPeriod = () => { setInvPeriodYear(''); setInvPeriodMonth(''); setInvPage(1) }
  const [showInvForm, setShowInvForm] = useState(false)
  const [editingInv, setEditingInv] = useState<any>(null)
  const [invForm, setInvForm] = useState({ ...EMPTY_INVOICE })
  const [invSaving, setInvSaving] = useState(false)
  const [viewingInv, setViewingInv] = useState<any>(null)

  // Search
  const [expSearch, setExpSearch] = useState('')
  const [invSearch, setInvSearch] = useState('')

  // Manage lists drawer
  const [showManageLists, setShowManageLists] = useState(false)
  const [manageListsContext, setManageListsContext] = useState<'expense' | 'invoice'>('expense')
  const [finSettings, setFinSettings] = useState<Record<string, string[]>>({})
  const [editingList, setEditingList] = useState<string | null>(null)
  const [newListItem, setNewListItem] = useState('')
  const [renamingItem, setRenamingItem] = useState<{ key: string, oldValue: string, newValue: string } | null>(null)
  const [deletingItem, setDeletingItem] = useState<string | null>(null)

  const fetchFinSettings = useCallback(() => {
    api.get('/finance/settings').then(r => setFinSettings(r.data)).catch(() => {})
  }, [])

  const fetchSpentByPending = useCallback(() => {
    api.get('/finance/expenses/spent-by-pending').then(r => setSpentByPending(r.data)).catch(() => {})
  }, [])

  const fetchAnalytics = useCallback(() => {
    setAnalyticsLoading(true)
    api.get(`/finance/analytics?yearType=${yearType}&year=${analyticsYear}&yearB=${analyticsYearB}`)
      .then(r => setAnalyticsData(r.data))
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false))
  }, [yearType, analyticsYear, analyticsYearB])

  useEffect(() => {
    if (activeTab === 'exp-analytics' || activeTab === 'inv-analytics') fetchAnalytics()
    if (activeTab === 'inv-analytics') {
      setAllYearsLoading(true)
      api.get(`/finance/invoices/all-years-sales?yearType=${yearType}`)
        .then(r => setAllYearsData(r.data))
        .catch(() => {})
        .finally(() => setAllYearsLoading(false))
    }
  }, [activeTab, fetchAnalytics, yearType])

  const saveListItem = async (key: string, items: string[]) => {
    try {
      await api.patch(`/finance/settings/${key}`, { values: items })
      setFinSettings(s => ({ ...s, [key]: items }))
      toast.success('List updated')
    } catch { toast.error('Failed to update') }
  }

  const addListItem = async (key: string) => {
    if (!newListItem.trim()) return
    const current = finSettings[key] || []
    if (current.includes(newListItem.trim())) { toast.error('Already exists'); return }
    await saveListItem(key, [...current, newListItem.trim()])
    setNewListItem('')
  }

  const removeListItem = async (key: string, item: string) => {
    const current = finSettings[key] || []
    await saveListItem(key, current.filter(i => i !== item))
  }

  const fetchSummary = useCallback(() => {
    if (!canExpense) return
    api.get('/finance/summary').then(r => setSummary(r.data)).catch(() => {})
  }, [canExpense])

  const fetchExpenses = useCallback(() => {
    if (!canExpense) return
    const params = new URLSearchParams({ page: String(expPage), limit: '20' })
    if (expStatus) params.append('status', expStatus)
    if (expCategory) params.append('category', expCategory)
    if (expPaymentMethod) params.append('paymentMethod', expPaymentMethod)
    if (expDateFrom) params.append('dateFrom', expDateFrom)
    if (expDateTo) params.append('dateTo', expDateTo)
    if (expSearch.trim()) params.append('search', expSearch.trim())
    if (expSpentBy) params.append('spentBy', expSpentBy)
    api.get(`/finance/expenses?${params}`).then(r => { setExpenses(r.data.entries); setExpTotal(r.data.pagination.total) }).catch(() => {})
  }, [canExpense, expPage, expStatus, expCategory, expPaymentMethod, expDateFrom, expDateTo, expSearch, expSpentBy])

  const fetchInvoices = useCallback(() => {
    if (!canInvoice) return
    const params = new URLSearchParams({ page: String(invPage), limit: '20' })
    if (invStatus) params.append('status', invStatus)
    if (invClient) params.append('clientName', invClient)
    if (invMode) params.append('modeOfTransaction', invMode)
    if (invPeriodYear) params.append('periodYear', invPeriodYear)
    if (invPeriodMonth) params.append('periodMonth', invPeriodMonth)
    if (invSearch.trim()) params.append('search', invSearch.trim())
    api.get(`/finance/invoices?${params}`).then(r => { setInvoices(r.data.entries); setInvTotal(r.data.pagination.total) }).catch(() => {})
  }, [canInvoice, invPage, invStatus, invClient, invMode, invPeriodYear, invPeriodMonth, invSearch])

  useEffect(() => { fetchSummary(); fetchExpenses(); fetchInvoices(); fetchFinSettings(); fetchSpentByPending() }, [fetchSummary, fetchExpenses, fetchInvoices, fetchFinSettings, fetchSpentByPending])
  useEffect(() => { setSelectedExpIds(new Set()) }, [expPage, expStatus, expCategory, expPaymentMethod, expDateFrom, expDateTo, expSearch, expSpentBy])

  const openExpForm = (exp?: any) => {
    if (exp) {
      setEditingExp(exp)
      setExpForm({
        vendorInvoiceNo: exp.vendorInvoiceNo || '',
        date: exp.date?.slice(0, 10) || '',
        description: exp.description,
        amount: exp.amount,
        paymentMethod: exp.paymentMethod,
        receiptType: exp.receiptType,
        authorizedBy: exp.authorizedBy,
        spentBy: exp.spentBy,
        status: exp.status,
        billUrl: exp.billUrl || '',
        category: exp.category,
        notes: exp.notes || ''
      })
    } else {
      setEditingExp(null)
      setExpForm({ ...EMPTY_EXPENSE, date: new Date().toISOString().slice(0, 10) })
    }
    setShowExpForm(true)
  }

  const saveExpense = async () => {
    if (!expForm.date || !expForm.description || !expForm.amount || !expForm.paymentMethod || !expForm.authorizedBy || !expForm.spentBy || !expForm.category) {
      toast.error('Please fill all required fields')
      return
    }
    setExpSaving(true)
    try {
      if (editingExp) {
        await api.patch(`/finance/expenses/${editingExp.id}`, expForm)
        toast.success('Expense updated')
      } else {
        await api.post('/finance/expenses', expForm)
        toast.success('Expense added')
      }
      setShowExpForm(false)
      fetchExpenses()
      fetchSummary()
    } catch { toast.error('Failed to save expense') }
    finally { setExpSaving(false) }
  }

  const deleteExpense = async (id: string) => {
    if (!window.confirm('Delete this expense entry?')) return
    try { await api.delete(`/finance/expenses/${id}`); toast.success('Deleted'); fetchExpenses(); fetchSummary() }
    catch { toast.error('Failed to delete') }
  }

  const bulkUpdateStatus = async (status: string) => {
    if (selectedExpIds.size === 0) return
    setBulkUpdating(true)
    try {
      await api.patch('/finance/expenses/bulk-status', { ids: Array.from(selectedExpIds), status })
      toast.success(`${selectedExpIds.size} expense${selectedExpIds.size === 1 ? '' : 's'} marked as ${status}`)
      setSelectedExpIds(new Set())
      fetchExpenses()
      fetchSummary()
    } catch { toast.error('Failed to update') }
    finally { setBulkUpdating(false) }
  }

  const openInvForm = (inv?: any) => {
    if (inv) {
      setEditingInv(inv)
      setInvForm({
        invoiceNo: inv.invoiceNo,
        clientName: inv.clientName,
        modeOfTransaction: inv.modeOfTransaction,
        invoicingPeriod: inv.invoicingPeriod,
        invoiceAmount: inv.invoiceAmount,
        status: inv.status,
        receivedAmount: inv.receivedAmount || '',
        raisedOn: inv.raisedOn?.slice(0, 10) || '',
        creditedInBank: inv.creditedInBank || '',
        receivedOn: inv.receivedOn?.slice(0, 10) || '',
        notes: inv.notes || ''
      })
    } else {
      setEditingInv(null)
      setInvForm({ ...EMPTY_INVOICE, raisedOn: new Date().toISOString().slice(0, 10) })
    }
    setShowInvForm(true)
  }

  const saveInvoice = async () => {
    if (!invForm.invoiceNo || !invForm.clientName || !invForm.modeOfTransaction || !invForm.invoicingPeriod || !invForm.invoiceAmount || !invForm.raisedOn) {
      toast.error('Please fill all required fields')
      return
    }
    setInvSaving(true)
    try {
      if (editingInv) {
        await api.patch(`/finance/invoices/${editingInv.id}`, invForm)
        toast.success('Invoice updated')
      } else {
        await api.post('/finance/invoices', invForm)
        toast.success('Invoice added')
      }
      setShowInvForm(false)
      fetchInvoices()
      fetchSummary()
    } catch { toast.error('Failed to save invoice') }
    finally { setInvSaving(false) }
  }

  const deleteInvoice = async (id: string) => {
    if (!window.confirm('Delete this invoice entry?')) return
    try { await api.delete(`/finance/invoices/${id}`); toast.success('Deleted'); fetchInvoices(); fetchSummary() }
    catch { toast.error('Failed to delete') }
  }

  const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#112240] text-sm text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200'
  const labelCls = 'text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block'

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Finance</h1>
        <p className="text-gray-400 mt-1">Track office expenses and client invoices</p>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Expenses This Month" value={`₹${parseFloat(summary.expenses.thisMonth).toLocaleString('en-IN')}`} color="bg-red-400" icon={TrendingDown} />
          <StatCard label="Total Expenses" value={`₹${parseFloat(summary.expenses.allTime).toLocaleString('en-IN')}`} sub="all time" color="bg-orange-400" icon={TrendingDown} />
          <StatCard label="Pending Expenses" value={summary.expenses.pendingCount} sub="awaiting payment" color="bg-amber-400" icon={Clock} />
          <StatCard label="Pending Invoices" value={summary.invoices.pendingCount} sub="awaiting receipt" color="bg-blue-400" icon={DollarSign} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-2xl p-1 mb-6 overflow-x-auto">
        {canExpAnalytics && (
          <button onClick={() => setActiveTab('exp-analytics')}
            className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeTab === 'exp-analytics' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
            Expense Analytics
          </button>
        )}
        {canInvAnalytics && (
          <button onClick={() => setActiveTab('inv-analytics')}
            className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeTab === 'inv-analytics' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
            Invoice Analytics
          </button>
        )}
        {canExpense && (
          <button onClick={() => setActiveTab('expenses')}
            className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeTab === 'expenses' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
            Expenses
          </button>
        )}
        {canInvoice && (
          <button onClick={() => setActiveTab('invoices')}
            className={`flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeTab === 'invoices' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
            Invoices
          </button>
        )}
      </div>

      {/* ── EXPENSES TAB ── */}
      {activeTab === 'expenses' && canExpense && (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="relative w-full sm:flex-1 sm:max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search expenses..." value={expSearch}
                  onChange={e => { setExpSearch(e.target.value); setExpPage(1) }}
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#112240] text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                {expSearch && <button onClick={() => { setExpSearch(''); setExpPage(1) }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X size={12} /></button>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap gap-2">
                <select value={expStatus} onChange={e => { setExpStatus(e.target.value); setExpPage(1) }}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  <option value="">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                </select>
                <select value={expCategory} onChange={e => { setExpCategory(e.target.value); setExpPage(1) }}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  <option value="">All Categories</option>
                  {(finSettings['expense_categories'] || CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={expPaymentMethod} onChange={e => { setExpPaymentMethod(e.target.value); setExpPage(1) }}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  <option value="">All Payment Methods</option>
                  {(finSettings['payment_methods'] || PAYMENT_METHODS).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {canManageExpense && (
                  <select value={expSpentBy} onChange={e => { setExpSpentBy(e.target.value); setExpPage(1) }}
                    className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                    <option value="">All Spent By</option>
                    {(finSettings['spent_by'] || SPENT_BY).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canManageExpense && (
                  <button onClick={() => { setManageListsContext('expense'); setShowManageLists(true); fetchFinSettings() }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1e3450] text-gray-500 hover:text-navy-700 text-sm transition-colors">
                    <Settings2 size={14} /> Manage Lists
                  </button>
                )}
                <button onClick={() => openExpForm()} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors">
                  <Plus size={14} /> Add Expense
                </button>
              </div>
            </div>
            {canManageExpense && expSpentBy && spentByPending[expSpentBy] && (
              <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-xl px-4 py-2.5">
                <div>
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">{expSpentBy}</span>
                  <span className="text-xs text-amber-500 mx-2">·</span>
                  <span className="text-xs text-amber-600 dark:text-amber-400">Pending: </span>
                  <span className="text-sm font-bold text-amber-600 dark:text-amber-400">₹{spentByPending[expSpentBy].amount.toLocaleString('en-IN')}</span>
                  <span className="text-xs text-amber-400 ml-2">({spentByPending[expSpentBy].count} entries)</span>
                </div>
              </div>
            )}
            {canManageExpense && expSpentBy && !spentByPending[expSpentBy] && (
              <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/20 rounded-xl px-4 py-2.5">
                <span className="text-xs text-green-600 font-medium">{expSpentBy} has no pending expenses</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <button onClick={setExpThisMonth} className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${expDateFrom && expDateTo && expDateFrom === new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10) ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                This Month
              </button>
              <button onClick={setExpLastMonth} className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${expDateFrom && expDateFrom === new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().slice(0, 10) ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                Last Month
              </button>
              <input type="date" value={expDateFrom} onChange={e => { setExpDateFrom(e.target.value); setExpPage(1) }}
                className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none" />
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={expDateTo} onChange={e => { setExpDateTo(e.target.value); setExpPage(1) }}
                className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none" />
              {(expDateFrom || expDateTo) && (
                <button onClick={clearExpDates} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Clear dates</button>
              )}
            </div>
          </div>

          

          {selectedExpIds.size > 0 && (
            <div className="flex items-center gap-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/20 rounded-xl px-4 py-2.5">
              <span className="text-xs font-medium text-orange-600">{selectedExpIds.size} selected</span>
              <div className="flex items-center gap-2 ml-2">
                <button onClick={() => bulkUpdateStatus('Paid')} disabled={bulkUpdating}
                  className="text-xs px-3 py-1.5 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium disabled:opacity-50 transition-colors">
                  {bulkUpdating ? 'Updating...' : 'Mark as Paid'}
                </button>
                <button onClick={() => bulkUpdateStatus('Pending')} disabled={bulkUpdating}
                  className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium disabled:opacity-50 transition-colors">
                  Mark as Pending
                </button>
                <button onClick={() => setSelectedExpIds(new Set())}
                  className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#112240] text-gray-500 hover:text-gray-700 transition-colors">
                  Clear
                </button>
              </div>
            </div>
          )}

          {!canManageExpense && (
            <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-xl px-4 py-2.5 mb-3">
              <span className="text-xs text-blue-600 dark:text-blue-400">You are viewing only your own expense entries.</span>
            </div>
          )}

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-[#112240] border-b border-gray-100 dark:border-[#1e3450]">
                  <tr>
                    <th className="px-4 py-3 w-8">
                      <input type="checkbox"
                        checked={expenses.length > 0 && selectedExpIds.size === expenses.length}
                        onChange={e => setSelectedExpIds(e.target.checked ? new Set(expenses.map((ex: any) => ex.id)) : new Set())}
                        className="w-3.5 h-3.5 accent-orange-500 cursor-pointer" />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Category</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Amount (₹)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Payment</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Auth By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Spent By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-[#1e3450]">
                  {expenses.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-300 text-sm">No expense entries yet</td></tr>
                  ) : expenses.map(e => (
                    <tr key={e.id} onClick={() => setViewingExp(e)} className={`hover:bg-orange-50/20 dark:hover:bg-orange-900/5 transition-colors cursor-pointer ${selectedExpIds.has(e.id) ? 'bg-orange-50/30 dark:bg-orange-900/10' : ''}`}>
                      <td className="px-4 py-3 w-8" onClick={ev => ev.stopPropagation()}>
                        <input type="checkbox"
                          checked={selectedExpIds.has(e.id)}
                          onChange={ev => {
                            const next = new Set(selectedExpIds)
                            ev.target.checked ? next.add(e.id) : next.delete(e.id)
                            setSelectedExpIds(next)
                          }}
                          className="w-3.5 h-3.5 accent-orange-500 cursor-pointer" />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{new Date(e.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className="px-4 py-3 text-navy-800 dark:text-white max-w-[200px]">
                        <p className="truncate">{e.description}</p>
                        {e.vendorInvoiceNo && <p className="text-[10px] text-gray-400">Inv: {e.vendorInvoiceNo}</p>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{e.category}</td>
                      <td className="px-4 py-3 font-mono font-medium text-navy-800 dark:text-white whitespace-nowrap">₹{parseFloat(e.amount).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{e.paymentMethod}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{e.authorizedBy}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{e.spentBy}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${e.status === 'Paid' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                          {e.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1" onClick={ev => ev.stopPropagation()}>
                            {e.billUrl && (
                              <a href={e.billUrl} target="_blank" rel="noreferrer"
                                className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors" title="View Bill">
                                <ExternalLink size={13} />
                              </a>
                            )}
                            <button onClick={() => openExpForm(e)} className="p-1.5 text-gray-400 hover:text-orange-500 transition-colors"><Pencil size={13} /></button>
                            <button onClick={() => deleteExpense(e.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                          </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {expTotal > 20 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-[#1e3450]">
                <p className="text-xs text-gray-400">{expTotal} total entries</p>
                <div className="flex items-center gap-2">
                  <button disabled={expPage === 1} onClick={() => setExpPage(p => p - 1)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#112240] disabled:opacity-40">Previous</button>
                  <span className="text-xs text-gray-400">Page</span>
                  <input type="number" min={1} max={Math.ceil(expTotal / 20)} value={expPage}
                    onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v >= 1 && v <= Math.ceil(expTotal / 20)) setExpPage(v) }}
                    className="w-12 text-center text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-white rounded-lg py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-orange-200" />
                  <span className="text-xs text-gray-400">of {Math.ceil(expTotal / 20)}</span>
                  <button disabled={expPage * 20 >= expTotal} onClick={() => setExpPage(p => p + 1)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#112240] disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── INVOICES TAB ── */}
      {activeTab === 'invoices' && canInvoice && (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="relative w-full sm:flex-1 sm:max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search invoices..." value={invSearch}
                  onChange={e => { setInvSearch(e.target.value); setInvPage(1) }}
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#112240] text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                {invSearch && <button onClick={() => { setInvSearch(''); setInvPage(1) }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"><X size={12} /></button>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap gap-2">
                <select value={invStatus} onChange={e => { setInvStatus(e.target.value); setInvPage(1) }}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  <option value="">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Received">Received</option>
                </select>
                <select value={invClient} onChange={e => { setInvClient(e.target.value); setInvPage(1) }}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  <option value="">All Clients</option>
                  {(finSettings['invoice_clients'] || CLIENTS).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={invMode} onChange={e => { setInvMode(e.target.value); setInvPage(1) }}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  <option value="">All Modes</option>
                  {(finSettings['transaction_modes'] || TRANSACTION_MODES).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setManageListsContext('invoice'); setShowManageLists(true); fetchFinSettings() }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1e3450] text-gray-500 hover:text-navy-700 text-sm transition-colors">
                  <Settings2 size={14} /> Manage Lists
                </button>
                <button onClick={() => openInvForm()} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors">
                  <Plus size={14} /> Add Invoice
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={setInvThisMonth} className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${invPeriodYear === String(new Date().getFullYear()) && invPeriodMonth === MONTHS[new Date().getMonth()] ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                This Month
              </button>
              <button onClick={setInvThisYear} className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${invPeriodYear === String(new Date().getFullYear()) && !invPeriodMonth ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 hover:bg-gray-200'}`}>
                This Year
              </button>
              <select value={invPeriodMonth} onChange={e => { setInvPeriodMonth(e.target.value); setInvPage(1) }}
                className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                <option value="">All Months</option>
                {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={invPeriodYear} onChange={e => { setInvPeriodYear(e.target.value); setInvPage(1) }}
                className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                <option value="">All Years</option>
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
              {(invPeriodYear || invPeriodMonth) && (
                <button onClick={clearInvPeriod} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Clear period</button>
              )}
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-[#112240] border-b border-gray-100 dark:border-[#1e3450]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Invoice No.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Client</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Period</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Mode</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Invoice ($)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Received ($)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Charges ($)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Credited (₹)</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Raised On</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-[#1e3450]">
                  {invoices.length === 0 ? (
                    <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-300 text-sm">No invoice entries yet</td></tr>
                  ) : invoices.map(inv => (
                    <tr key={inv.id} onClick={() => setViewingInv(inv)} className="hover:bg-orange-50/20 dark:hover:bg-orange-900/5 transition-colors cursor-pointer">
                      <td className="px-4 py-3 font-mono text-xs text-orange-500 font-medium whitespace-nowrap">{inv.invoiceNo}</td>
                      <td className="px-4 py-3 text-navy-800 dark:text-white whitespace-nowrap">{inv.clientName}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{inv.invoicingPeriod}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{inv.modeOfTransaction}</td>
                      <td className="px-4 py-3 font-mono font-medium text-navy-800 dark:text-white">${parseFloat(inv.invoiceAmount).toFixed(2)}</td>
                      <td className="px-4 py-3 font-mono text-green-600 hidden sm:table-cell">{inv.receivedAmount ? `$${parseFloat(inv.receivedAmount).toFixed(2)}` : '—'}</td>
                      <td className="px-4 py-3 font-mono text-red-400 hidden sm:table-cell">{inv.additionalCharges != null ? `$${parseFloat(inv.additionalCharges).toFixed(2)}` : '—'}</td>
                      <td className="px-4 py-3 font-mono text-navy-800 dark:text-white hidden sm:table-cell">{inv.creditedInBank ? `₹${parseFloat(inv.creditedInBank).toLocaleString('en-IN')}` : '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${inv.status === 'Received' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap hidden sm:table-cell">{new Date(inv.raisedOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1" onClick={ev => ev.stopPropagation()}>
                            <button onClick={() => openInvForm(inv)} className="p-1.5 text-gray-400 hover:text-orange-500 transition-colors"><Pencil size={13} /></button>
                            <button onClick={() => deleteInvoice(inv.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                          </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {invTotal > 20 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-[#1e3450]">
                <p className="text-xs text-gray-400">{invTotal} total entries</p>
                <div className="flex items-center gap-2">
                  <button disabled={invPage === 1} onClick={() => setInvPage(p => p - 1)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#112240] disabled:opacity-40">Previous</button>
                  <span className="text-xs text-gray-400">Page</span>
                  <input type="number" min={1} max={Math.ceil(invTotal / 20)} value={invPage}
                    onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v >= 1 && v <= Math.ceil(invTotal / 20)) setInvPage(v) }}
                    className="w-12 text-center text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-white rounded-lg py-1.5 border-none focus:outline-none focus:ring-2 focus:ring-orange-200" />
                  <span className="text-xs text-gray-400">of {Math.ceil(invTotal / 20)}</span>
                  <button disabled={invPage * 20 >= invTotal} onClick={() => setInvPage(p => p + 1)} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-[#112240] disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── EXPENSE FORM MODAL ── */}
      {((activeTab === 'exp-analytics' && canExpAnalytics) || (activeTab === 'inv-analytics' && canInvAnalytics)) && (
        <div className="space-y-6">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-xl p-1 w-full sm:w-auto">
              <button onClick={() => setYearType('FY')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${yearType === 'FY' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
                FY (Apr-Mar)
              </button>
              <button onClick={() => setYearType('CY')}
                className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${yearType === 'CY' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500'}`}>
                CY (Jan-Dec)
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Year A:</span>
              <select value={analyticsYear} onChange={e => setAnalyticsYear(parseInt(e.target.value))}
                className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{yearType === 'FY' ? `FY ${y}-${String(y + 1).slice(-2)}` : String(y)}</option>
                ))}
              </select>
            </div>
            {activeTab === 'inv-analytics' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">vs Year B:</span>
                <select value={analyticsYearB} onChange={e => setAnalyticsYearB(parseInt(e.target.value))}
                  className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                  {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{yearType === 'FY' ? `FY ${y}-${String(y + 1).slice(-2)}` : String(y)}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {analyticsLoading ? (
            <div className="py-20 text-center text-gray-400">Loading analytics...</div>
          ) : !analyticsData ? (
            <div className="py-20 text-center text-gray-300">No data available</div>
          ) : (
            <>
              {/* Summary totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {activeTab === 'exp-analytics' && (
                  <div className="card p-4">
                    <p className="text-xs text-gray-400">Total Expenses</p>
                    <p className="text-xl font-bold text-red-500">₹{analyticsData.totals.totalExpenses.toLocaleString('en-IN')}</p>
                  </div>
                )}
                {activeTab === 'inv-analytics' && <>
                  <div className="card p-4">
                    <p className="text-xs text-gray-400">Total Invoiced (Year A)</p>
                    <p className="text-xl font-bold text-blue-500">${analyticsData.totals.totalInvoiced.toFixed(2)}</p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-gray-400">Total Invoiced (Year B)</p>
                    <p className="text-xl font-bold text-blue-300">${analyticsData.totals.prevInvoicedElapsed.toFixed(2)}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">same {analyticsData.totals.elapsedMonthsCount} month{analyticsData.totals.elapsedMonthsCount === 1 ? '' : 's'} period</p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-gray-400">Total Received (Year A)</p>
                    <p className="text-xl font-bold text-green-500">${analyticsData.totals.totalReceived.toFixed(2)}</p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-gray-400">Transactional Charges (Year A)</p>
                    <p className="text-xl font-bold text-red-400">${(analyticsData.totals.totalTransactionalCharges || 0).toFixed(2)}</p>
                  </div>
                  <div className="card p-4">
                    <p className="text-xs text-gray-400">Credited in Bank (Year A)</p>
                    <p className="text-xl font-bold text-teal-500">₹{(analyticsData.totals.totalCreditedINR || 0).toLocaleString('en-IN')}</p>
                  </div>
                    </>}
                    </div>

              {/* ── EXPENSE ANALYTICS ── */}
              {activeTab === 'exp-analytics' && (() => {
                const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } = require('recharts')
                const COLORS = ['#f87171','#fb923c','#fbbf24','#a3e635','#34d399','#22d3ee','#60a5fa','#a78bfa','#f472b6','#94a3b8','#f97316','#84cc16']
                return (
                  <>
                    {/* Monthly Expense Bar Chart */}
                    <div className="card p-5">
                      <p className="font-display font-semibold text-navy-900 dark:text-white mb-4">Monthly Expenses (₹)</p>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={analyticsData.expenseByMonth} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={(v: number) => `₹${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Amount']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          <Bar dataKey="amount" fill="#f87171" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Category Pie + Bar side by side */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="card p-5">
                        <p className="font-display font-semibold text-navy-900 dark:text-white mb-4">Expenses by Category</p>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={analyticsData.expenseByCategory} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ category, percent }: any) => `${category} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                              {analyticsData.expenseByCategory.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Amount']} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="card p-5">
                        <p className="font-display font-semibold text-navy-900 dark:text-white mb-4">Category Breakdown</p>
                        <div className="space-y-2 overflow-y-auto max-h-52">
                          {analyticsData.expenseByCategory.map((c: any, i: number) => {
                            const total = analyticsData.totals.totalExpenses || 1
                            const pct = Math.round((c.amount / total) * 100)
                            return (
                              <div key={c.category} className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                <span className="text-xs text-gray-500 w-24 truncate flex-shrink-0">{c.category}</span>
                                <div className="flex-1 bg-gray-100 dark:bg-[#112240] rounded-full h-1.5">
                                  <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                                </div>
                                <span className="text-xs font-mono text-gray-400 flex-shrink-0 w-16 text-right">₹{(c.amount/1000).toFixed(1)}k</span>
                              </div>
                            )
                          })}
                          {analyticsData.expenseByCategory.length === 0 && <p className="text-xs text-gray-300 text-center py-4">No data</p>}
                        </div>
                      </div>
                    </div>
                  </>
                )
              })()}

              {/* ── INVOICE ANALYTICS ── */}
              {activeTab === 'inv-analytics' && (() => {
                const { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } = require('recharts')
                const yearALabel = yearType === 'FY' ? `FY ${analyticsYear}-${String(analyticsYear + 1).slice(-2)}` : String(analyticsYear)
                const yearBLabel = yearType === 'FY' ? `FY ${analyticsYearB}-${String(analyticsYearB + 1).slice(-2)}` : String(analyticsYearB)
                const CLIENT_COLORS = ['#60a5fa','#f87171','#34d399','#fbbf24','#a78bfa','#f472b6','#22d3ee','#fb923c']

                // Build overall monthly comparison data
                const overallData = analyticsData.months.map((month: string, i: number) => ({
                  month,
                  [yearALabel]: analyticsData.invoiceByMonth[i]?.invoiced || 0,
                  [yearBLabel]: analyticsData.invoiceByMonth[i]?.prevInvoiced || 0,
                }))

                // Build per-client monthly data for Year A
                const clients: string[] = analyticsData.uniqueClients || analyticsData.revenueByClient.map((c: any) => c.client)
                const clientMonthlyData = analyticsData.months.map((month: string, i: number) => {
                  const row: any = { month }
                  clients.forEach((client: string) => {
                    row[`${client} (${yearALabel})`] = analyticsData.invoiceByMonth[i]?.[`client_${client}`] || 0
                    row[`${client} (${yearBLabel})`] = analyticsData.invoiceByMonth[i]?.[`prev_client_${client}`] || 0
                  })
                  return row
                })

                return (
                  <>
                    {/* Overall Sales Line Chart -- 70% chart + 30% table */}
                    <div className="card p-5">
                      <p className="font-display font-semibold text-navy-900 dark:text-white mb-1">Overall Sales ($)</p>
                      <p className="text-xs text-gray-400 mb-4">{yearALabel} vs {yearBLabel}</p>
                      <div className="flex gap-4">
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <ResponsiveContainer width="100%" height={280}>
                            <LineChart data={overallData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                              <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v: number) => `$${v}`} />
                              <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                              <Legend formatter={(v: string) => <span style={{ fontSize: 10 }}>{v}</span>} />
                              <Line type="monotone" dataKey={yearALabel} stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                              <Line type="monotone" dataKey={yearBLabel} stroke="#93c5fd" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="w-full sm:w-56 sm:flex-shrink-0 overflow-x-auto sm:overflow-y-auto" style={{ maxHeight: 280 }}>
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white dark:bg-[#0f2137]">
                              <tr>
                                <th className="text-left py-1.5 text-gray-400 font-semibold">Month</th>
                                <th className="text-right py-1.5 text-blue-500 font-semibold truncate max-w-[60px]">{yearALabel}</th>
                                <th className="text-right py-1.5 text-blue-300 font-semibold truncate max-w-[60px]">{yearBLabel}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-[#1e3450]">
                              {overallData.map((row: any) => (
                                <tr key={row.month} className="hover:bg-gray-50 dark:hover:bg-[#112240]">
                                  <td className="py-1.5 text-gray-500 font-medium">{row.month}</td>
                                  <td className="py-1.5 text-right font-mono text-navy-800 dark:text-white">{row[yearALabel] > 0 ? `$${row[yearALabel].toFixed(0)}` : '—'}</td>
                                  <td className="py-1.5 text-right font-mono text-gray-400">{row[yearBLabel] > 0 ? `$${row[yearBLabel].toFixed(0)}` : '—'}</td>
                                </tr>
                              ))}
                              <tr className="border-t-2 border-gray-200 dark:border-[#1e3450]">
                                <td className="py-1.5 font-bold text-navy-800 dark:text-white">Total</td>
                                <td className="py-1.5 text-right font-mono font-bold text-blue-500">${overallData.reduce((s: number, r: any) => s + r[yearALabel], 0).toFixed(0)}</td>
                                <td className="py-1.5 text-right font-mono font-bold text-blue-300">${overallData.reduce((s: number, r: any) => s + r[yearBLabel], 0).toFixed(0)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Individual Client Line Chart -- 70% chart + 30% table */}
                    <div className="card p-5">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-display font-semibold text-navy-900 dark:text-white">Individual Client Sales ($)</p>
                        <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}
                          className="text-xs bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-300 rounded-lg px-2.5 py-1.5 border-none focus:outline-none">
                          <option value="">Select a client</option>
                          {clients.map((c: string) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <p className="text-xs text-gray-400 mb-4">
                        {selectedClient ? `${selectedClient} — ${yearALabel} (solid) vs ${yearBLabel} (dashed)` : 'Select a client to see year-on-year comparison'}
                      </p>
                      {selectedClient ? (
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1" style={{ minWidth: 0 }}>
                            <ResponsiveContainer width="100%" height={250}>
                              <LineChart data={clientMonthlyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v: number) => `$${v}`} />
                                <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                                <Legend formatter={(v: string) => <span style={{ fontSize: 10 }}>{v}</span>} />
                                <Line type="monotone" dataKey={`${selectedClient} (${yearALabel})`} stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                <Line type="monotone" dataKey={`${selectedClient} (${yearBLabel})`} stroke="#93c5fd" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} activeDot={{ r: 4 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="w-56 flex-shrink-0 overflow-y-auto" style={{ maxHeight: 280 }}>
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-white dark:bg-[#0f2137]">
                                <tr>
                                  <th className="text-left py-1.5 text-gray-400 font-semibold">Month</th>
                                  <th className="text-right py-1.5 text-blue-500 font-semibold truncate max-w-[60px]">{yearALabel}</th>
                                  <th className="text-right py-1.5 text-blue-300 font-semibold truncate max-w-[60px]">{yearBLabel}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50 dark:divide-[#1e3450]">
                                {clientMonthlyData.map((row: any) => (
                                  <tr key={row.month} className="hover:bg-gray-50 dark:hover:bg-[#112240]">
                                    <td className="py-1.5 text-gray-500 font-medium">{row.month}</td>
                                    <td className="py-1.5 text-right font-mono text-navy-800 dark:text-white">{row[`${selectedClient} (${yearALabel})`] > 0 ? `$${row[`${selectedClient} (${yearALabel})`].toFixed(0)}` : '—'}</td>
                                    <td className="py-1.5 text-right font-mono text-gray-400">{row[`${selectedClient} (${yearBLabel})`] > 0 ? `$${row[`${selectedClient} (${yearBLabel})`].toFixed(0)}` : '—'}</td>
                                  </tr>
                                ))}
                                <tr className="border-t-2 border-gray-200 dark:border-[#1e3450]">
                                  <td className="py-1.5 font-bold text-navy-800 dark:text-white">Total</td>
                                  <td className="py-1.5 text-right font-mono font-bold text-blue-500">${clientMonthlyData.reduce((s: number, r: any) => s + (r[`${selectedClient} (${yearALabel})`] || 0), 0).toFixed(0)}</td>
                                  <td className="py-1.5 text-right font-mono font-bold text-blue-300">${clientMonthlyData.reduce((s: number, r: any) => s + (r[`${selectedClient} (${yearBLabel})`] || 0), 0).toFixed(0)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="h-40 flex items-center justify-center text-gray-300 dark:text-gray-600 text-sm">
                          Select a client from the dropdown above
                        </div>
                      )}
                    </div>

                    {/* All Years Sales Comparison -- after Individual Client */}
                    <div className="card p-5">
                      <p className="font-display font-semibold text-navy-900 dark:text-white mb-1">All Years Sales Comparison ($)</p>
                      <p className="text-xs text-gray-400 mb-4">Monthly sales across all available years</p>
                      {allYearsLoading ? (
                        <div className="py-10 text-center text-gray-400 text-sm">Loading...</div>
                      ) : !allYearsData ? (
                        <div className="py-10 text-center text-gray-300 text-sm">No data</div>
                      ) : (() => {
                        const { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer: AYResponsive, Legend } = require('recharts')
                        const YEAR_COLORS = ['#60a5fa','#f87171','#34d399','#fbbf24','#a78bfa','#f472b6','#22d3ee']
                        return (
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div style={{ width: '45%', minWidth: 0 }} className="hidden sm:block">
                              <AYResponsive width="100%" height={260}>
                                <LineChart data={allYearsData.data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                  <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#9ca3af' }} />
                                  <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} tickFormatter={(v: number) => `$${v}`} width={45} />
                                  <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                                  <Legend formatter={(v: string) => <span style={{ fontSize: 9 }}>{v}</span>} />
                                  {allYearsData.years.map((yr: string, i: number) => (
                                    <Line key={yr} type="monotone" dataKey={yr} stroke={YEAR_COLORS[i % YEAR_COLORS.length]} strokeWidth={1.5} dot={{ r: 1.5 }} activeDot={{ r: 3 }} />
                                  ))}
                                </LineChart>
                              </AYResponsive>
                            </div>
                            <div className="flex-1 overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-white dark:bg-[#0f2137]">
                                  <tr>
                                    <th className="text-left py-1.5 pr-3 text-gray-400 font-semibold whitespace-nowrap">Month</th>
                                    {allYearsData.years.map((yr: string, i: number) => (
                                      <th key={yr} className="text-right py-1.5 px-2 font-semibold whitespace-nowrap" style={{ color: YEAR_COLORS[i % YEAR_COLORS.length] }}>{yr}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-[#1e3450]">
                                  {allYearsData.data.map((row: any) => (
                                    <tr key={row.month} className="hover:bg-gray-50 dark:hover:bg-[#112240]">
                                      <td className="py-1.5 pr-3 text-gray-500 font-medium whitespace-nowrap">{row.month}</td>
                                      {allYearsData.years.map((yr: string, i: number) => (
                                        <td key={yr} className="py-1.5 px-2 text-right font-mono text-navy-800 dark:text-white whitespace-nowrap">
                                          {row[yr] > 0 ? `$${row[yr].toFixed(0)}` : '—'}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                  <tr className="border-t-2 border-gray-200 dark:border-[#1e3450]">
                                    <td className="py-1.5 pr-3 font-bold text-navy-800 dark:text-white">Total</td>
                                    {allYearsData.years.map((yr: string, i: number) => (
                                      <td key={yr} className="py-1.5 px-2 text-right font-mono font-bold whitespace-nowrap" style={{ color: YEAR_COLORS[i % YEAR_COLORS.length] }}>
                                        ${allYearsData.data.reduce((s: number, r: any) => s + (r[yr] || 0), 0).toFixed(0)}
                                      </td>
                                    ))}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                    {/* Client Revenue + Invoice Status side by side */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="card p-5 flex-[3]">
                        <p className="font-display font-semibold text-navy-900 dark:text-white mb-1">Client Revenue ($)</p>
                        <p className="text-xs text-gray-400 mb-4">{yearALabel} vs {yearBLabel}</p>
                        <div className="space-y-3">
                        {analyticsData.revenueByClient.map((c: any, i: number) => {
                          const max = Math.max(...analyticsData.revenueByClient.map((x: any) => Math.max(x.invoiced, x.prevInvoiced)), 1)
                          const pctA = Math.round((c.invoiced / max) * 100)
                          const pctB = Math.round((c.prevInvoiced / max) * 100)
                          return (
                            <div key={c.client} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-navy-800 dark:text-white">{c.client}</span>
                                <div className="flex items-center gap-2 text-[10px] font-mono">
                                  <span style={{ color: CLIENT_COLORS[i % CLIENT_COLORS.length] }}>${c.invoiced.toFixed(0)}</span>
                                  <span className="text-gray-300">vs</span>
                                  <span className="text-gray-400">${c.prevInvoiced.toFixed(0)}</span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-100 dark:bg-[#112240] rounded-full h-1.5 relative">
                                <div className="absolute h-1.5 rounded-full opacity-40" style={{ width: `${pctB}%`, backgroundColor: CLIENT_COLORS[i % CLIENT_COLORS.length] }} />
                                <div className="absolute h-1.5 rounded-full" style={{ width: `${pctA}%`, backgroundColor: CLIENT_COLORS[i % CLIENT_COLORS.length] }} />
                              </div>
                            </div>
                          )
                        })}
                        {analyticsData.revenueByClient.length === 0 && <p className="text-xs text-gray-300 text-center py-4">No data</p>}
                        </div>
                      </div>
                      <div className="card p-5 flex-[2]">
                        <p className="font-display font-semibold text-navy-900 dark:text-white mb-4">Invoice Status -- {yearALabel} ($)</p>
                      <div className="flex flex-col sm:flex-row items-center gap-6">
                        <ResponsiveContainer width={180} height={180}>
                          <PieChart>
                            <Pie data={[
                              { name: 'Received', value: analyticsData.pendingVsPaid.invoices.paid },
                              { name: 'Pending', value: analyticsData.pendingVsPaid.invoices.pending }
                            ]} dataKey="value" cx="50%" cy="50%" outerRadius={70}>
                              <Cell fill="#4ade80" />
                              <Cell fill="#fbbf24" />
                            </Pie>
                            <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-3 flex-1">
                          <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/10 rounded-xl px-4 py-3">
                            <span className="text-sm text-green-600 font-medium">Received</span>
                            <span className="text-lg font-bold text-green-600">${analyticsData.pendingVsPaid.invoices.paid.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/10 rounded-xl px-4 py-3">
                            <span className="text-sm text-amber-500 font-medium">Pending</span>
                            <span className="text-lg font-bold text-amber-500">${analyticsData.pendingVsPaid.invoices.pending.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </>
                )
              })()}
            </>
          )}
        </div>
      )}

      {/* Expense Detail Popup */}
      {viewingExp && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setViewingExp(null)}>
          <div className="bg-white dark:bg-[#0f2137] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1e3450] flex-shrink-0">
              <div>
                <p className="font-display font-semibold text-navy-900 dark:text-white">{viewingExp.voucherNo}</p>
                <p className="text-xs text-gray-400 mt-0.5">{viewingExp.category}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${viewingExp.status === 'Paid' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>{viewingExp.status}</span>
                <button onClick={() => setViewingExp(null)} className="text-gray-300 hover:text-gray-500 transition-colors"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400">Voucher No.</p><p className="text-sm font-mono font-medium text-orange-500">{viewingExp.voucherNo}</p></div>
                <div><p className="text-xs text-gray-400">Date</p><p className="text-sm font-medium text-navy-800 dark:text-white">{new Date(viewingExp.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>
                <div><p className="text-xs text-gray-400">Amount</p><p className="text-sm font-bold text-navy-800 dark:text-white">₹{parseFloat(viewingExp.amount).toLocaleString('en-IN')}</p></div>
                <div><p className="text-xs text-gray-400">Payment Method</p><p className="text-sm text-navy-800 dark:text-white">{viewingExp.paymentMethod}</p></div>
                <div><p className="text-xs text-gray-400">Receipt Type</p><p className="text-sm text-navy-800 dark:text-white">{viewingExp.receiptType}</p></div>
                <div><p className="text-xs text-gray-400">Authorized By</p><p className="text-sm text-navy-800 dark:text-white">{viewingExp.authorizedBy}</p></div>
                <div><p className="text-xs text-gray-400">Spent By</p><p className="text-sm text-navy-800 dark:text-white">{viewingExp.spentBy}</p></div>
                {viewingExp.vendorInvoiceNo && <div><p className="text-xs text-gray-400">Vendor Invoice No.</p><p className="text-sm text-navy-800 dark:text-white">{viewingExp.vendorInvoiceNo}</p></div>}
              </div>
              {viewingExp.description && (
                <div><p className="text-xs text-gray-400 mb-1">Description</p><p className="text-sm text-navy-800 dark:text-white bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3">{viewingExp.description}</p></div>
              )}
              {viewingExp.notes && (
                <div><p className="text-xs text-gray-400 mb-1">Notes</p><p className="text-sm text-navy-800 dark:text-white bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3">{viewingExp.notes}</p></div>
              )}
              {viewingExp.billUrl && (
                <div><p className="text-xs text-gray-400 mb-1">Bill Attachment</p>
                  <a href={viewingExp.billUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-500 hover:text-blue-600 text-sm transition-colors">
                    <ExternalLink size={14} /> View Bill on Google Drive
                  </a>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-[#1e3450] flex-shrink-0">
              <button onClick={() => { deleteExpense(viewingExp.id); setViewingExp(null) }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 text-sm transition-colors"><Trash2 size={14} /> Delete</button>
              <button onClick={() => { openExpForm(viewingExp); setViewingExp(null) }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors"><Pencil size={14} /> Edit</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Invoice Detail Popup */}
      {viewingInv && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setViewingInv(null)}>
          <div className="bg-white dark:bg-[#0f2137] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1e3450] flex-shrink-0">
              <div>
                <p className="font-display font-semibold text-navy-900 dark:text-white">{viewingInv.invoiceNo}</p>
                <p className="text-xs text-gray-400 mt-0.5">{viewingInv.clientName} · {viewingInv.invoicingPeriod}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${viewingInv.status === 'Received' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>{viewingInv.status}</span>
                <button onClick={() => setViewingInv(null)} className="text-gray-300 hover:text-gray-500 transition-colors"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs text-gray-400">Invoice Amount</p><p className="text-lg font-bold text-blue-500">${parseFloat(viewingInv.invoiceAmount).toFixed(2)}</p></div>
                <div><p className="text-xs text-gray-400">Received Amount</p><p className="text-lg font-bold text-green-500">{viewingInv.receivedAmount ? `$${parseFloat(viewingInv.receivedAmount).toFixed(2)}` : '—'}</p></div>
                {viewingInv.receivedAmount && (
                  <div><p className="text-xs text-gray-400">Transaction Charges</p><p className="text-sm font-bold text-red-400">${(parseFloat(viewingInv.invoiceAmount) - parseFloat(viewingInv.receivedAmount)).toFixed(2)}</p></div>
                )}
                {viewingInv.creditedInBank && (
                  <div><p className="text-xs text-gray-400">Credited in Bank</p><p className="text-sm font-bold text-teal-500">₹{parseFloat(viewingInv.creditedInBank).toLocaleString('en-IN')}</p></div>
                )}
                <div><p className="text-xs text-gray-400">Mode of Transaction</p><p className="text-sm text-navy-800 dark:text-white">{viewingInv.modeOfTransaction}</p></div>
                <div><p className="text-xs text-gray-400">Raised On</p><p className="text-sm text-navy-800 dark:text-white">{new Date(viewingInv.raisedOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>
                {viewingInv.receivedOn && <div><p className="text-xs text-gray-400">Received On</p><p className="text-sm text-navy-800 dark:text-white">{new Date(viewingInv.receivedOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div>}
              </div>
              {viewingInv.notes && (
                <div><p className="text-xs text-gray-400 mb-1">Notes</p><p className="text-sm text-navy-800 dark:text-white bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3">{viewingInv.notes}</p></div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-[#1e3450] flex-shrink-0">
              <button onClick={() => { deleteInvoice(viewingInv.id); setViewingInv(null) }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 text-sm transition-colors"><Trash2 size={14} /> Delete</button>
              <button onClick={() => { openInvForm(viewingInv); setViewingInv(null) }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors"><Pencil size={14} /> Edit</button>
            </div>
          </div>
        </div>
      , document.body)}

      {showManageLists && createPortal(
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/40" onClick={() => setShowManageLists(false)} />
          <div className="w-full max-w-sm bg-white dark:bg-[#0f2137] h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-[#1e3450] flex-shrink-0">
              <p className="font-display font-semibold text-navy-900 dark:text-white">{manageListsContext === 'expense' ? 'Expense Lists' : 'Invoice Lists'}</p>
              <button onClick={() => setShowManageLists(false)} className="text-gray-300 hover:text-gray-500"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {[
                { key: 'expense_categories', label: 'Expense Categories', context: 'expense' },
                { key: 'payment_methods', label: 'Payment Methods', context: 'expense' },
                { key: 'receipt_types', label: 'Receipt Types', context: 'expense' },
                { key: 'authorized_by', label: 'Authorized By', context: 'expense' },
                { key: 'spent_by', label: 'Spent By', context: 'expense' },
                { key: 'invoice_clients', label: 'Invoice Clients', context: 'invoice' },
                { key: 'transaction_modes', label: 'Transaction Modes', context: 'invoice' },
              ].filter(item => item.context === manageListsContext).map(({ key, label }) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">{label}</p>
                    <button onClick={() => { setEditingList(editingList === key ? null : key); setRenamingItem(null); setDeletingItem(null) }}
                      className="text-xs text-gray-400 hover:text-orange-500 transition-colors">
                      {editingList === key ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {(finSettings[key] || []).map((item, idx) => (
                      <div key={item}>
                        {renamingItem?.key === key && renamingItem?.oldValue === item ? (
                          <div className="flex gap-2">
                            <input type="text" value={renamingItem.newValue}
                              onChange={e => setRenamingItem(r => r ? { ...r, newValue: e.target.value } : null)}
                              onKeyDown={async e => {
                                if (e.key === 'Enter' && renamingItem.newValue.trim() && renamingItem.newValue !== item) {
                                  await api.post(`/finance/settings/${key}/rename`, { oldValue: item, newValue: renamingItem.newValue.trim() })
                                  fetchFinSettings()
                                  setRenamingItem(null)
                                  toast.success('Renamed')
                                }
                                if (e.key === 'Escape') setRenamingItem(null)
                              }}
                              autoFocus
                              className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-orange-300 bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                            <button onClick={async () => {
                              if (renamingItem.newValue.trim() && renamingItem.newValue !== item) {
                                await api.post(`/finance/settings/${key}/rename`, { oldValue: item, newValue: renamingItem.newValue.trim() })
                                fetchFinSettings()
                                toast.success('Renamed')
                              }
                              setRenamingItem(null)
                            }} className="px-2 py-1.5 rounded-lg bg-orange-500 text-white text-xs">Save</button>
                            <button onClick={() => setRenamingItem(null)} className="px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-[#112240] text-gray-500 text-xs">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-gray-50 dark:bg-[#112240] rounded-lg px-3 py-2 group">
                            <span className="text-sm text-navy-800 dark:text-white flex-1">{item}</span>
                            {editingList === key && (
                              <div className="flex items-center gap-1 ml-2">
                                {/* Move up */}
                                <button disabled={idx === 0} onClick={async () => {
                                  const items = [...(finSettings[key] || [])]
                                  const tmp = items[idx - 1]; items[idx - 1] = items[idx]; items[idx] = tmp
                                  await saveListItem(key, items)
                                }} className="p-1 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors" title="Move up">↑</button>
                                {/* Move down */}
                                <button disabled={idx === (finSettings[key] || []).length - 1} onClick={async () => {
                                  const items = [...(finSettings[key] || [])]
                                  const tmp = items[idx + 1]; items[idx + 1] = items[idx]; items[idx] = tmp
                                  await saveListItem(key, items)
                                }} className="p-1 text-gray-300 hover:text-gray-500 disabled:opacity-20 transition-colors" title="Move down">↓</button>
                                {/* Rename */}
                                <button onClick={() => { setRenamingItem({ key, oldValue: item, newValue: item }); setDeletingItem(null) }}
                                  className="p-1 text-gray-300 hover:text-orange-500 transition-colors" title="Rename"><Pencil size={11} /></button>
                                {/* Delete */}
                                <button onClick={async () => {
                                  if (deletingItem === item) {
                                    // Second click -- check if in use
                                    const res = await api.get(`/finance/settings/check-in-use?key=${key}&value=${encodeURIComponent(item)}`)
                                    if (res.data.inUse) {
                                      toast.error(`Cannot delete "${item}" — it is used in ${res.data.count} existing record${res.data.count === 1 ? '' : 's'}. Rename it instead.`)
                                      setDeletingItem(null)
                                    } else {
                                      await removeListItem(key, item)
                                      setDeletingItem(null)
                                    }
                                  } else {
                                    setDeletingItem(item)
                                    setTimeout(() => setDeletingItem(null), 3000)
                                  }
                                }}
                                  className={`p-1 transition-colors ${deletingItem === item ? 'text-red-500 animate-pulse' : 'text-gray-300 hover:text-red-500'}`}
                                  title={deletingItem === item ? 'Click again to confirm delete' : 'Delete'}>
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {editingList === key && (
                      <div className="flex gap-2 mt-2">
                        <input type="text" placeholder={`Add to ${label}...`} value={newListItem}
                          onChange={e => setNewListItem(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addListItem(key) }}
                          className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200" />
                        <button onClick={() => addListItem(key)} className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium transition-colors">Add</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      , document.body)}

      {showExpForm && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowExpForm(false)}>
          <div className="bg-white dark:bg-[#0f2137] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1e3450]">
              <p className="font-display font-semibold text-navy-900 dark:text-white">{editingExp ? 'Edit Expense' : 'Add Expense'}</p>
              <button onClick={() => setShowExpForm(false)} className="text-gray-300 hover:text-gray-500"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Date <span className="text-red-400">*</span></label>
                <input type="date" value={expForm.date} onChange={e => setExpForm(f => ({ ...f, date: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Vendor Invoice No.</label>
                <input type="text" placeholder="INV-001" value={expForm.vendorInvoiceNo} onChange={e => setExpForm(f => ({ ...f, vendorInvoiceNo: e.target.value }))} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Description <span className="text-red-400">*</span></label>
                <input type="text" placeholder="What was this expense for?" value={expForm.description} onChange={e => setExpForm(f => ({ ...f, description: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Amount (₹) <span className="text-red-400">*</span></label>
                <input type="number" placeholder="0.00" value={expForm.amount} onChange={e => setExpForm(f => ({ ...f, amount: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Category <span className="text-red-400">*</span></label>
                <select value={expForm.category} onChange={e => setExpForm(f => ({ ...f, category: e.target.value }))} className={inputCls}>
                  <option value="">Select category</option>
                  {(finSettings['expense_categories'] || CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Payment Method <span className="text-red-400">*</span></label>
                <select value={expForm.paymentMethod} onChange={e => setExpForm(f => ({ ...f, paymentMethod: e.target.value }))} className={inputCls}>
                  <option value="">Select method</option>
                  {(finSettings['payment_methods'] || PAYMENT_METHODS).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Receipt Type</label>
                <select value={expForm.receiptType} onChange={e => setExpForm(f => ({ ...f, receiptType: e.target.value }))} className={inputCls}>
                  {(finSettings['receipt_types'] || RECEIPT_TYPES).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Authorized By <span className="text-red-400">*</span></label>
                <select value={expForm.authorizedBy} onChange={e => setExpForm(f => ({ ...f, authorizedBy: e.target.value }))} className={inputCls}>
                  <option value="">Select director</option>
                  {(finSettings['authorized_by'] || DIRECTORS).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Spent By <span className="text-red-400">*</span></label>
                <select value={expForm.spentBy} onChange={e => setExpForm(f => ({ ...f, spentBy: e.target.value }))} className={inputCls}>
                  {(finSettings['spent_by'] || SPENT_BY).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={expForm.status} onChange={e => setExpForm(f => ({ ...f, status: e.target.value }))} className={inputCls}>
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Bill Attachment (Google Drive)</label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/10 rounded-xl px-3 py-2.5">
                    <FileText size={14} className="text-blue-500 flex-shrink-0" />
                    <p className="text-xs text-blue-600 dark:text-blue-400 flex-1">Upload your bill to Google Drive, then right-click the file → Share → Copy link, and paste it below.</p>
                    <a href="https://drive.google.com/drive/folders/1aPQfTWKv0RJJqoeeVeQpLWw4y7qMRBVr?usp=drive_link" target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0">
                      <ExternalLink size={11} /> Open Drive
                    </a>
                  </div>
                  <input type="text" placeholder="Paste Google Drive share link here..." value={expForm.billUrl}
                    onChange={e => setExpForm(f => ({ ...f, billUrl: e.target.value }))}
                    className={inputCls} />
                  {expForm.billUrl && expForm.billUrl.includes('google.com') && (
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/10 rounded-lg px-3 py-2">
                      <CheckCircle size={13} className="text-green-500 flex-shrink-0" />
                      <span className="text-xs text-green-600 flex-1">Drive link attached</span>
                      <a href={expForm.billUrl} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium transition-colors">
                        <ExternalLink size={11} /> View Bill
                      </a>
                    </div>
                  )}
                  {expForm.billUrl && !expForm.billUrl.includes('google.com') && !expForm.billUrl.includes('drive.google') && (
                    <p className="text-[10px] text-amber-500 px-1">This doesn't look like a Google Drive link -- please check the URL</p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes</label>
                <textarea rows={2} placeholder="Any additional notes..." value={expForm.notes} onChange={e => setExpForm(f => ({ ...f, notes: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-[#1e3450]">
              <button onClick={() => setShowExpForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              <button onClick={saveExpense} disabled={expSaving} className="px-5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                {expSaving ? 'Saving...' : editingExp ? 'Update' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* ── INVOICE FORM MODAL ── */}
      {showInvForm && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowInvForm(false)}>
          <div className="bg-white dark:bg-[#0f2137] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1e3450]">
              <p className="font-display font-semibold text-navy-900 dark:text-white">{editingInv ? 'Edit Invoice' : 'Add Invoice'}</p>
              <button onClick={() => setShowInvForm(false)} className="text-gray-300 hover:text-gray-500"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Invoice No. <span className="text-red-400">*</span></label>
                <input type="text" placeholder="INK/006/2026-27" value={invForm.invoiceNo} onChange={e => setInvForm(f => ({ ...f, invoiceNo: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Client <span className="text-red-400">*</span></label>
                <select value={invForm.clientName} onChange={e => setInvForm(f => ({ ...f, clientName: e.target.value }))} className={inputCls}>
                  <option value="">Select client</option>
                  {(finSettings['invoice_clients'] || CLIENTS).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Invoicing Period <span className="text-red-400">*</span></label>
                <div className="flex gap-2">
                  <select
                    value={invForm.invoicingPeriod ? invForm.invoicingPeriod.split(' ')[0] : ''}
                    onChange={e => {
                      const month = e.target.value
                      const year = invForm.invoicingPeriod ? invForm.invoicingPeriod.split(' ')[1] || String(new Date().getFullYear()) : String(new Date().getFullYear())
                      setInvForm(f => ({ ...f, invoicingPeriod: month ? `${month} ${year}` : '' }))
                    }}
                    className={inputCls}>
                    <option value="">Month</option>
                    {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select
                    value={invForm.invoicingPeriod ? invForm.invoicingPeriod.split(' ')[1] || '' : ''}
                    onChange={e => {
                      const year = e.target.value
                      const month = invForm.invoicingPeriod ? invForm.invoicingPeriod.split(' ')[0] || '' : ''
                      setInvForm(f => ({ ...f, invoicingPeriod: month ? `${month} ${year}` : '' }))
                    }}
                    className={inputCls}>
                    <option value="">Year</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Mode of Transaction <span className="text-red-400">*</span></label>
                <select value={invForm.modeOfTransaction} onChange={e => setInvForm(f => ({ ...f, modeOfTransaction: e.target.value }))} className={inputCls}>
                  <option value="">Select mode</option>
                  {(finSettings['transaction_modes'] || TRANSACTION_MODES).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Invoice Amount ($) <span className="text-red-400">*</span></label>
                <input type="number" step="0.01" placeholder="0.00" value={invForm.invoiceAmount} onChange={e => setInvForm(f => ({ ...f, invoiceAmount: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Raised On <span className="text-red-400">*</span></label>
                <input type="date" value={invForm.raisedOn} onChange={e => setInvForm(f => ({ ...f, raisedOn: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={invForm.status} onChange={e => setInvForm(f => ({ ...f, status: e.target.value }))} className={inputCls}>
                  <option value="Pending">Pending</option>
                  <option value="Received">Received</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Received Amount ($)</label>
                <input type="number" step="0.01" placeholder="0.00" value={invForm.receivedAmount} onChange={e => setInvForm(f => ({ ...f, receivedAmount: e.target.value }))} className={inputCls} />
              </div>
              {invForm.invoiceAmount && invForm.receivedAmount && (
                <div className="sm:col-span-2 bg-red-50 dark:bg-red-900/10 rounded-xl px-4 py-2.5 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Transaction charges (auto-calculated)</span>
                  <span className="text-sm font-mono font-semibold text-red-500">
                    ${(parseFloat(invForm.invoiceAmount) - parseFloat(invForm.receivedAmount)).toFixed(2)}
                  </span>
                </div>
              )}
              <div>
                <label className={labelCls}>Credited in Bank (₹)</label>
                <input type="number" step="0.01" placeholder="0.00" value={invForm.creditedInBank} onChange={e => setInvForm(f => ({ ...f, creditedInBank: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Received On</label>
                <input type="date" value={invForm.receivedOn} onChange={e => setInvForm(f => ({ ...f, receivedOn: e.target.value }))} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes</label>
                <textarea rows={2} placeholder="Any additional notes..." value={invForm.notes} onChange={e => setInvForm(f => ({ ...f, notes: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-[#1e3450]">
              <button onClick={() => setShowInvForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              <button onClick={saveInvoice} disabled={invSaving} className="px-5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                {invSaving ? 'Saving...' : editingInv ? 'Update' : 'Add Invoice'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  )
}
